import ImagePlaceholderTsoPq from '@components/ImagePlaceholder/ImagePlaceholderTsoPq';
import LoadingSpinnerTsoPq from '@components/LoadingSpinners/LoadingSpinnerTsoPq';
import { useAppSelector } from '@redux/hooks';
import { useTranslation } from 'react-i18next';

import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { Chart, type ChartOptions } from '@highcharts/react';
import '@highcharts/react/modules/Boost';
import '@highcharts/react/series/Heatmap';
import { Box, styled } from '@mui/material';
import { getTsoMeta } from '@pages/ImportedMeasurementsOverview/ImportedMeasurementsOverviewChart';
import { useGetMeasurementDeviceGetAllQuery } from '@redux/apiSlices/measurementDeviceApi';
import {
  selectCorrelationCounts,
  selectIsLoading,
  selectOriginalCalculationParameters,
  selectSelectedCalculation,
  selectSingleCoefficients,
} from '@redux/slices/correlationSlice';
import type { AxisLabelsFormatterContextObject, Point } from 'highcharts/highcharts.src';
import { useMemo } from 'react';

const StyledHeatMapContainer = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flexGrow: 1,
    minWidth: 0,
    position: 'relative',
  };
});
const StyledHeatMapChartTitle = styled(Box)(() => {
  return {
    width: '100%',
  };
});
const StyledHeatMapChartSubtitle = styled(Box)(() => {
  return {
    width: '100%',
    paddingBottom: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_24,
  };
});

type CorrelationRecordSingle = {
  measurementDevice1Id: number;
  measurementDevice2Id: number;
  value: number | null;
};

type CorrelationRecordMultiple = {
  measurementDevice1Id: number;
  measurementDevice2Id: number;
  correlatingMeasurementParameters: string[];
};

type CorrelationRecord = CorrelationRecordSingle | CorrelationRecordMultiple;

type RawCorrelationRecord = {
  measurementDevice1Id?: number;
  measurementDevice2Id?: number;
  value?: number | null;
  correlatingMeasurementParameters?: string[] | null;
};

type DeviceMeta = {
  color: string;
  label: string;
};

type HeatmapPoint = {
  technicalNames: string[];
  value: number | null;
  x: number;
  y: number;
};

type HeatmapLookupValue = Pick<HeatmapPoint, 'technicalNames' | 'value'>;

const createAxisLabelFormatter = (axisLabels: string[], deviceMeta: DeviceMeta[]) =>
  function axisLabelFormatter(this: AxisLabelsFormatterContextObject) {
    const index = Number(this.pos);
    const meta = deviceMeta[index];
    const label = axisLabels[index] ?? String(this.value);

    if (!meta) {
      return label;
    }

    return `<span style="color:${meta.color}">\u25CF</span> ${label}`;
  };

const getTechnicalNamesLabel = (technicalNames: string[], rawValue: number | null, t: (key: string) => string) => {
  if (technicalNames.length > 0) {
    return technicalNames.join(', ');
  }

  if (rawValue === 0) {
    return '';
  }

  return t('correlation.tooltip.none');
};

const getTechnicalNames = (record: CorrelationRecordMultiple) =>
  record.correlatingMeasurementParameters.filter((name): name is string => Boolean(name));

const hasMeasurementDeviceIds = (
  record: RawCorrelationRecord
): record is RawCorrelationRecord & { measurementDevice1Id: number; measurementDevice2Id: number } =>
  record.measurementDevice1Id != null && record.measurementDevice2Id != null;

const toCorrelationRecords = (data: RawCorrelationRecord[], calculationType: number): CorrelationRecord[] =>
  data.filter(hasMeasurementDeviceIds).map((record) => {
    const { measurementDevice1Id, measurementDevice2Id } = record;

    if (calculationType === 0) {
      return {
        measurementDevice1Id,
        measurementDevice2Id,
        value: record.value ?? null,
      };
    }

    return {
      measurementDevice1Id,
      measurementDevice2Id,
      correlatingMeasurementParameters: record.correlatingMeasurementParameters ?? [],
    };
  });

const getDeviceIds = (data: CorrelationRecord[]) =>
  Array.from(new Set(data.flatMap((record) => [record.measurementDevice1Id, record.measurementDevice2Id])));

const getHeatmapLookupValue = (record: CorrelationRecord, calculationType: number): HeatmapLookupValue => {
  if (calculationType === 0) {
    const { value } = record as CorrelationRecordSingle;

    return {
      technicalNames: [],
      value: value == null ? null : value * 100,
    };
  }

  const technicalNames = getTechnicalNames(record as CorrelationRecordMultiple);

  return {
    technicalNames,
    value: technicalNames.length,
  };
};

const createHeatmapLookup = (data: CorrelationRecord[], calculationType: number) => {
  const lookup = new Map<string, HeatmapLookupValue>();

  for (const record of data) {
    const { measurementDevice1Id, measurementDevice2Id } = record;
    const value = getHeatmapLookupValue(record, calculationType);

    lookup.set(`${measurementDevice1Id}-${measurementDevice2Id}`, value);
    lookup.set(`${measurementDevice2Id}-${measurementDevice1Id}`, value);
  }

  return lookup;
};

const createHeatmapPoints = (deviceIds: number[], lookup: Map<string, HeatmapLookupValue>) => {
  const points: HeatmapPoint[] = [];

  for (let rowIndex = 0; rowIndex < deviceIds.length; rowIndex++) {
    for (let columnIndex = 0; columnIndex < deviceIds.length; columnIndex++) {
      if (rowIndex === columnIndex) continue;

      const rowId = deviceIds[rowIndex];
      const columnId = deviceIds[columnIndex];
      const lookupValue = lookup.get(`${rowId}-${columnId}`);

      points.push({
        technicalNames: lookupValue?.technicalNames ?? [],
        x: columnIndex,
        y: rowIndex,
        value: lookupValue?.value ?? null,
      });
    }
  }

  return points;
};

const prepareHeatmapData = (data: CorrelationRecord[], calculationType: number) => {
  const ids = getDeviceIds(data);
  const lookup = createHeatmapLookup(data, calculationType);

  return {
    ids,
    points: createHeatmapPoints(ids, lookup),
  };
};

const createTooltipFormatter = ({
  axisLabels,
  calculationType,
  selectedParametersLength,
  t,
}: {
  axisLabels: string[];
  calculationType: number;
  selectedParametersLength: number;
  t: (key: string) => string;
}) =>
  function tooltipFormatter(this: Point) {
    const point = this as Point & HeatmapPoint;
    const technicalNames = Array.isArray((point.options as HeatmapPoint).technicalNames)
      ? (point.options as HeatmapPoint).technicalNames
      : [];

    const seriesName = `${axisLabels[point.y]} - ${axisLabels[point.x]}`;
    const rawValue = point.value;
    const technicalNamesLabel = getTechnicalNamesLabel(technicalNames, rawValue, t);
    const content = [`<b>${seriesName}</b>`];

    if (point.value === null) {
      content.push(t('correlation.couldNotCalculate'));
    } else if (calculationType === 0) {
      content.push(`${t('correlation.value')}: ${rawValue}%`);
    } else {
      content.push(`${rawValue} ${t('correlation.tooltip1')} ${selectedParametersLength} ${t('correlation.tooltip2')}`);

      if (technicalNamesLabel) {
        content.push(technicalNamesLabel);
      }
    }

    return content.join('<br/>');
  };

const CorrelationChart = () => {
  const { t } = useTranslation();
  const isLoading = useAppSelector(selectIsLoading);
  const originalCalculation = useAppSelector(selectOriginalCalculationParameters);
  const singleCorrelation = useAppSelector(selectSingleCoefficients);
  const multipleCorrelation = useAppSelector(selectCorrelationCounts);
  const selectedCalculation = useAppSelector(selectSelectedCalculation);
  const calculationType = originalCalculation.type;
  const selectedParameters = originalCalculation.selectedParameters || [];
  const selectedDeviceCount = Object.values(originalCalculation.deviceTechnicalNames).reduce(
    (acc, arr) => acc + arr.length,
    0
  );

  const { data: allDevicesData, isLoading: isDevicesLoading } = useGetMeasurementDeviceGetAllQuery();

  const correlationData = calculationType === 0 ? singleCorrelation : multipleCorrelation;
  const correlationLoaded = correlationData.length > 0;

  const deviceNameById = useMemo(() => {
    const list = allDevicesData?.externalDtos ?? [];
    const map = new Map<number, string>();

    for (const device of list) {
      map.set(device.id!, device.name);
    }

    return map;
  }, [allDevicesData]);

  const { ids, points } = prepareHeatmapData(
    toCorrelationRecords(calculationType === 0 ? singleCorrelation : multipleCorrelation, calculationType),
    calculationType
  );

  const deviceMeta = ids.map((deviceId) => {
    const device = allDevicesData?.externalDtos?.find((item) => item.id === deviceId);

    if (!device) {
      return {
        label: String(deviceId),
        color: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_dark,
      };
    }

    const tsoMeta = device.tsoId ? getTsoMeta(device.tsoId) : undefined;

    return {
      label: device.name,
      color: tsoMeta?.color ?? tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_dark,
    };
  });

  const axisLabels = ids.map((id) => deviceNameById.get(id) ?? id.toString());
  const areDeviceLabelsReady = ids.every((id) => deviceNameById.has(id));

  const options: ChartOptions = {
    chart: {
      type: 'heatmap',
      marginTop: 24,
      height: '50%',
      animation: false,
      backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
      plotBackgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
    },
    boost: { useGPUTranslations: true },
    title: { text: '' },
    xAxis: {
      visible: axisLabels.length <= 20,
      categories: axisLabels,
      labels: {
        reserveSpace: true,
        useHTML: false,
        formatter: createAxisLabelFormatter(axisLabels, deviceMeta),
        style: { fontFamily: 'OpenSans', fontWeight: '400', fontSize: '0.75rem' },
      },
    },
    yAxis: {
      visible: axisLabels.length <= 20,
      title: { text: '' },
      categories: axisLabels,
      labels: {
        reserveSpace: true,
        useHTML: false,
        formatter: createAxisLabelFormatter(axisLabels, deviceMeta),
        style: { fontFamily: 'OpenSans', fontWeight: '400', fontSize: '1rem' },
      },
    },
    colorAxis: {
      min: calculationType === 0 ? -100 : 0,
      max: calculationType === 0 ? 100 : selectedParameters.length,
      stops:
        calculationType === 0
          ? [
              [0, '#5E8FD9'],
              [0.5, tsoPqTheme.palette.background.other.color_other_background_white],
              [1, '#DB5E5C'], // red for pozitive
            ]
          : [
              [0, tsoPqTheme.palette.background.other.color_other_background_white],
              [1, '#5E8FD9'],
            ],
    },
    legend: {
      symbolWidth: 375,
      title: {
        text: calculationType === 0 ? t('correlation.legend.single') : t('correlation.legend.multiple'),
        style: {
          fontFamily: 'OpenSans',
          fontWeight: '400',
          textAlign: 'center',
          fontSize: '0.875rem',
        },
      },
    },
    series: [
      {
        type: 'heatmap',
        animation: false,
        boostThreshold: 100,
        data: points,
        borderWidth: 1,
        borderColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_dark,
        nullColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_accent,
        dataLabels: {
          enabled: points.length < 400,
          color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
          format: '{point.value:.0f}',
          style: {
            fontWeight: '400',
            fontSize: '0.75rem',
            textOutline: 'none',
          },
          allowOverlap: true,
          crop: false,
          overflow: 'allow',
        },
      },
    ],
    tooltip: {
      backgroundColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_enabled,
      style: {
        color: tsoPqTheme.palette.customTextColor.color_font_ft_white,
        fontFamily: 'OpenSans',
        fontWeight: '400',
        fontSize: '0.75rem',
        padding: tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_12,
        zIndex: '1000',
      },
      formatter: createTooltipFormatter({
        axisLabels,
        calculationType,
        selectedParametersLength: selectedParameters.length,
        t,
      }),
    },
    credits: {
      enabled: false,
    },
  };

  if (isLoading) {
    return <LoadingSpinnerTsoPq />;
  }

  if (correlationLoaded && ids.length > 0 && !areDeviceLabelsReady && isDevicesLoading) {
    return <LoadingSpinnerTsoPq />;
  }

  if (!selectedCalculation && !correlationLoaded) {
    return (
      <ImagePlaceholderTsoPq
        title={t('correlation.placeholderTitle')}
        description={t('correlation.placeholderDescription')}
      />
    );
  }

  return (
    <StyledHeatMapContainer>
      <StyledHeatMapChartTitle>
        <TypographyTsoPq typographyType="sectionTitleMDBold">{t('correlation.matrix')}</TypographyTsoPq>
      </StyledHeatMapChartTitle>
      {correlationLoaded && ids.length !== selectedDeviceCount && (
        <StyledHeatMapChartSubtitle>
          <TypographyTsoPq typographyType="pageSubtitleSM">{t('correlation.matrix.description')}</TypographyTsoPq>
        </StyledHeatMapChartSubtitle>
      )}
      <Chart options={options} />
    </StyledHeatMapContainer>
  );
};

export default CorrelationChart;
